import { put, takeLatest, call, delay } from 'redux-saga/effects'
import { reportsActions } from './ReportsActions'
import { reportsReducers } from './ReportsSlice'
import ReportsService, {
  AI_REPORT_POLL_INTERVAL_MS,
  AI_REPORT_POLL_MAX_ATTEMPTS,
} from './ReportsService'
import camelize from 'camelize'
import columnNames from './columnNames.json'
import { ReportType, ReportGettingType } from './types'
import wordings from './wordings.json'
import fileDownload from 'js-file-download'
import { mapAiReportToTable } from './helperFunction'

function* setSelectedSegmentsSaga(action: any) {
  yield put(reportsReducers.setSelectedSegments(action.payload))
}

function* setReportTypeSaga(action: any) {
  yield put(reportsReducers.setReportType(action.payload))
}

function* setStartDateSaga(action: any) {
  yield put(reportsReducers.setStartDate(action.payload))
}

function* setEndDateSaga(action: any) {
  yield put(reportsReducers.setEndDate(action.payload))
}

function* gettingReportSaga(reportGettingType: ReportGettingType) {
  if (reportGettingType === 'csv') {
    yield put(reportsReducers.setIsFileDownloading(true))
    return
  }
  yield put(reportsReducers.setTableLoading(true))
  yield put(reportsReducers.clearTableData())
  yield put(reportsReducers.setTableMessage(wordings.generatingReport))
}

function* gettingReportErrorSaga() {
  yield put(reportsReducers.setTableLoading(false))
  yield put(reportsReducers.setTableMessage(wordings.errorFetchingData))
}

function* gettingReportSuccessSaga(rows: any, reportType: ReportType) {
  yield put(reportsReducers.setTableData(rows))
  yield put(reportsReducers.setHeaderCells(columnNames[reportType]))
  yield put(reportsReducers.setTableLoading(false))
}

function* noCommuterDataSaga() {
  yield put(reportsReducers.setTableMessage(wordings.noCommuterData))
  yield put(reportsReducers.setTableLoading(false))
}

function* downloadCsv(data: any, reportType: ReportType) {
  fileDownload(data, `${reportType}.csv`)
}

function* getOverallReportSaga(action: any) {
  const { reportType, reportGettingType } = action.payload
  yield call(gettingReportSaga, reportGettingType)
  try {
    const report = yield call(ReportsService.getOverallReport, action.payload)

    // If reportGettingType is csv, download the file and do nothing else
    if (reportGettingType === 'csv') {
      yield call(downloadCsv, report.data, reportType)
      yield put(reportsReducers.setIsFileDownloading(false))
      return
    }

    if (report.status === 200) {
      const reportData = camelize(report.data)
      const { Commuters } = reportData.tablesData
      if (Commuters && Commuters.rows.length > 0) {
        const { rows } = Commuters
        const columnName = columnNames[reportType]
        const tableData = rows.map((textArray) =>
          textArray.map((text, index) => ({
            text,
            customCell: columnName[index].customCell || null,
          }))
        )
        yield call(gettingReportSuccessSaga, tableData, reportType)
      } else {
        yield call(noCommuterDataSaga)
      }
    } else {
      yield call(gettingReportErrorSaga)
    }
  } catch (error) {
    yield call(gettingReportErrorSaga)
  }
}

function* getRemainingReportsSaga(action: any) {
  const { reportType, reportGettingType } = action.payload
  yield call(gettingReportSaga, reportGettingType)
  try {
    const report = yield call(
      ReportsService.getRemainingReports,
      action.payload
    )

    // If reportGettingType is csv, download the file and do nothing else
    if (reportGettingType === 'csv') {
      yield call(downloadCsv, report.data, reportType)
      yield put(reportsReducers.setIsFileDownloading(false))
      return
    }

    if (report.status === 200) {
      const reportData = camelize(report.data)
      if (reportData.length > 0) {
        const tableData = reportData.map((object) =>
          columnNames[reportType].map((column) => {
            const text = object[column.key]
            const customCell = column.customCell ? column.customCell : null
            return { text, customCell }
          })
        )
        yield call(gettingReportSuccessSaga, tableData, reportType)
      } else {
        yield call(noCommuterDataSaga)
      }
    } else {
      yield call(gettingReportErrorSaga)
    }
  } catch (error) {
    yield call(gettingReportErrorSaga)
  }
}

function parseAiReportError(_error: unknown): string {
  return wordings.aiReportError
}

function mapAiReportColumns(
  columns: Array<{ key?: string; display_name?: string; displayName?: string }>
) {
  return (columns || [])
    .filter((column) => column?.key)
    .map((column) => ({
      key: column.key as string,
      displayName: column.displayName || column.display_name || column.key,
    }))
}

function* failAiReport(message: string) {
  yield put(reportsReducers.setAiReportError(message))
  yield put(reportsReducers.setTableMessage(message))
  yield put(reportsReducers.setTableLoading(false))
}

export function* generateAiReportSaga(action: { payload: { prompt: string } }) {
  yield put(reportsReducers.setAiReportLoading(true))
  yield put(reportsReducers.setTableLoading(true))
  yield put(reportsReducers.clearTableData())
  yield put(reportsReducers.setTableMessage(wordings.generatingAiReport))
  try {
    const started = yield call(
      ReportsService.generateAiReport,
      action.payload.prompt
    )
    const jobId = started?.data?.job_id || started?.data?.jobId
    if ((started.status !== 202 && started.status !== 200) || !jobId) {
      yield call(failAiReport, wordings.aiReportError)
      return
    }

    let attempts = 0
    while (true) {
      let report
      try {
        report = yield call(ReportsService.getAiReportJob, jobId)
      } catch (_error) {
        attempts += 1
        if (attempts >= AI_REPORT_POLL_MAX_ATTEMPTS) {
          yield call(failAiReport, wordings.aiReportError)
          return
        }
        yield delay(AI_REPORT_POLL_INTERVAL_MS)
        continue
      }
      const status = report?.data?.status
      if (status === 'pending') {
        attempts += 1
        if (attempts >= AI_REPORT_POLL_MAX_ATTEMPTS) {
          yield call(failAiReport, wordings.aiReportError)
          return
        }
        yield delay(AI_REPORT_POLL_INTERVAL_MS)
        continue
      }
      if (report.status !== 200 || status === 'error') {
        yield call(failAiReport, wordings.aiReportError)
        return
      }
      const columns = mapAiReportColumns(report.data.columns)
      const { headerCells, tableData } = mapAiReportToTable(
        columns,
        report.data.rows || []
      )
      if (tableData.length === 0) {
        yield put(reportsReducers.setHeaderCells(headerCells))
        yield put(reportsReducers.setTableMessage(wordings.aiReportNoData))
        yield put(reportsReducers.setTableLoading(false))
        yield put(reportsReducers.setAiReportSuccess())
        return
      }
      yield put(reportsReducers.setHeaderCells(headerCells))
      yield put(reportsReducers.setTableData(tableData))
      yield put(reportsReducers.setTableLoading(false))
      yield put(reportsReducers.setAiReportSuccess())
      return
    }
  } catch (error) {
    const message = parseAiReportError(error)
    yield call(failAiReport, message)
  }
}

export function* clearAiReportErrorSaga() {
  yield put(reportsReducers.resetAiReportStatus())
}

export function* reportsSaga() {
  yield takeLatest(
    reportsActions.changeSelectedSegments.type,
    setSelectedSegmentsSaga
  )
  yield takeLatest(reportsActions.changeReportType.type, setReportTypeSaga)
  yield takeLatest(reportsActions.changeStartDate.type, setStartDateSaga)
  yield takeLatest(reportsActions.changeEndDate.type, setEndDateSaga)
  yield takeLatest(reportsActions.getOverallReport.type, getOverallReportSaga)
  yield takeLatest(
    reportsActions.getRemainingReports.type,
    getRemainingReportsSaga
  )
  yield takeLatest(reportsActions.generateAiReport.type, generateAiReportSaga)
  yield takeLatest(
    reportsActions.clearAiReportError.type,
    clearAiReportErrorSaga
  )
}

export default reportsSaga
